import { Howl } from "howler"
import { useEffect, useRef, useState } from "react"
import { getBeatSrc } from "shared/utils/getBeatSrc"
import { SITE_TITLE } from "shared/constants/textConstants"

const COVER_ART_SRC = `${process.env.PUBLIC_URL}/favicons/android-chrome-512x512.png`

function setLockScreenMetadata(beat, stop) {
  if (!("mediaSession" in navigator)) return

  navigator.mediaSession.metadata = new window.MediaMetadata({
    title: beat.file,
    artist: SITE_TITLE,
    album: SITE_TITLE,
    artwork: [
      { src: COVER_ART_SRC, sizes: "512x512", type: "image/png" },
      { src: `${process.env.PUBLIC_URL}/favicons/android-chrome-192x192.png`, sizes: "192x192", type: "image/png" }
    ]
  })
  navigator.mediaSession.playbackState = "playing"
  navigator.mediaSession.setActionHandler("pause", stop)
  navigator.mediaSession.setActionHandler("stop", stop)
}

function clearLockScreenMetadata() {
  if (!("mediaSession" in navigator)) return
  navigator.mediaSession.playbackState = "none"
  navigator.mediaSession.metadata = null
}

export default function useAudioController({ beat, playingBeat, setPlayingBeat, stop }) {
  const [isLoading, setIsLoading] = useState(false)
  const beatEntity = useRef(null)
  const playingBeatRef = useRef(playingBeat)
  const retriedRef = useRef(false)

  useEffect(() => {
    playingBeatRef.current = playingBeat
  }, [playingBeat])

  const setup = () => {
    if (beatEntity.current || beat === null) return
    beatEntity.current = new Howl({
      src: getBeatSrc(beat),
      loop: true,
      // A real HTML5 <audio> element, never Web Audio API: on iOS, sound
      // generated through Web Audio (AudioContext/AudioBufferSourceNode) is
      // treated as an "ambient" sound — muted by the physical silent switch
      // and suspended when the screen locks. A real <audio> element counts
      // as media playback, so it ignores the silent switch and (with the
      // Media Session API wiring below) keeps playing behind the lock
      // screen. It also streams progressively — playback can start as soon
      // as there's enough buffered, instead of waiting on the whole file.
      html5: true,
      autoSuspend: false,
      preload: false,
      onload() {
        retriedRef.current = false
        setIsLoading(false)
      },
      onloaderror() {
        // A load can fail transiently (a cold service worker, a Range
        // request racing the cache, etc.). One automatic retry with a
        // fresh <audio> node clears most of these up, instead of leaving
        // the pad silently stuck until the user presses it a second time.
        if (!retriedRef.current) {
          retriedRef.current = true
          beatEntity.current?.unload()
          beatEntity.current = null
          setup()
          if (playingBeatRef.current === beat.id) beatEntity.current.play()
          return
        }
        setIsLoading(false)
      }
    })
  }

  const click = () => {
    if (!beatEntity.current) return
    if (playingBeat === beat.id) {
      beatEntity.current.stop()
      beatEntity.current.play()
    } else {
      setIsLoading(true)
      setPlayingBeat(beat.id)
    }
  }

  useEffect(() => {
    if (playingBeat !== beat?.id && beatEntity.current) {
      beatEntity.current.stop()
      beatEntity.current.unload()
      setIsLoading(false)
      retriedRef.current = false
    } else if (beatEntity.current) {
      beatEntity.current.play()
      setLockScreenMetadata(beat, stop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingBeat])

  useEffect(() => {
    setup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!playingBeat) clearLockScreenMetadata()
  }, [playingBeat])

  return { isLoading, click }
}
